<template>
	<div v-if="isEnabled" :id="elementId" ref="container" class="min-h-px"></div>
</template>

<script setup lang="ts">
import {useLocalization} from "@pkg/shared/composables/use-localization";

type FlowboxFunction = (...args: unknown[]) => void;

declare global {
	interface Window {
		flowbox?: FlowboxFunction;
	}
}

const properties = defineProps<{
	flowboxKey: string;
	tags?: string[] | undefined;
}>();

const elementId = `js-flowbox-flow-${useId()}`;
const container = useTemplateRef<HTMLElement>("container");
let observer: IntersectionObserver | undefined;

const {languageTag} = useLocalization();

const isEnabled = computed(
	() => properties.flowboxKey && properties.flowboxKey.length > 0,
);

function flowboxOptions(flowboxKey: string, tags?: string[]) {
	const options: Record<string, unknown> = {
		container: `#${elementId}`,
		key: flowboxKey,
		locale: languageTag,
	};

	if (tags) {
		options.tags = tags;
	}

	return options;
}

let flowboxScriptPromise: Promise<boolean> | undefined;

// Load the script, de-duplicated, and resolve when loaded
function ensureFlowboxScript() {
	if (window.flowbox) {
		return Promise.resolve(true);
	}

	flowboxScriptPromise ??= new Promise((resolve) => {
		const existingScript = document.querySelector<HTMLScriptElement>(
			"script[data-masku-flowbox]",
		);
		const script = existingScript || document.createElement("script");

		script.addEventListener("load", () => resolve(true), {once: true});
		script.addEventListener("error", () => resolve(false), {once: true});

		if (!existingScript) {
			script.src = "https://connect.getflowbox.com/flowbox.js";
			script.async = true;
			script.dataset.maskuFlowbox = "true";
			document.head.append(script);
		}
	});

	return flowboxScriptPromise;
}

async function initializeFlowbox() {
	if (!isEnabled.value || !(await ensureFlowboxScript())) {
		return;
	}

	window.flowbox?.(
		"init",
		flowboxOptions(properties.flowboxKey, properties.tags),
	);
}

// We load the flowbox script based on the container element's visibility to the user, so that the script is only loaded if the element ever comes to view. This might avoid processing unnecessary JS from flowbox's side, since it's not necessarily ever shown to all users.
onMounted(() => {
	if (!container.value || !isEnabled.value) {
		return;
	}

	if (!("IntersectionObserver" in window)) {
		initializeFlowbox();
		return;
	}

	observer = new IntersectionObserver(
		(entries) => {
			if (!entries.some((entry) => entry.isIntersecting)) {
				return;
			}

			observer?.disconnect();
			initializeFlowbox();
		},
		{
			rootMargin: "800px 0px",
		},
	);

	observer.observe(container.value);
});

onBeforeUnmount(() => observer?.disconnect());
</script>
